import React, { useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useData } from "../context/DataContext";
import { cellText } from "../utils/helpers";
import CustomSelect from "./CustomSelect";

const RECURRING_TYPES = ["Not Applicable","Daily","Weekly","Monthly","Quarterly","Half Yearly","Yearly"];
const PRIORITIES      = ["Low","Medium","High"];
const DEPARTMENTS     = ["HR & ADMIN", "ACCOUNT & FINANCE", "PROJECT EXECUTION", "PURCHASE", "TENDER & MARKETING"];
const COMPANIES       = ["Aquint Infratele India Pvt Ltd", "Arkonik Fibretech India Pvt Ltd", "Aquint Global Techskill India Pvt Ltd"];

function toDatetimeLocal(ms) {
  if (!ms) return "";
  const d = new Date(typeof ms === "number" ? ms : parseInt(ms));
  return d.toISOString().slice(0, 16);
}

function getSupervisorId(val) {
  if (!val) return "";
  if (Array.isArray(val)) return val[0]?.id || "";
  if (val?.users) return val.users[0]?.id || "";
  if (val?.id) return val.id;
  return "";
}

function getAssignedUserIds(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(v => v?.id || v).filter(Boolean);
  if (val?.users) return val.users.map(u => u.id).filter(Boolean);
  return [];
}

export default function EditTaskModal({ record, tableId, onClose }) {
  const { updateTask, larkUsers, getAttachments, deleteAttachment, uploadFile } = useData();
  const f = record.fields || {};
  const rid = record.record_id;

  const [form, setForm] = useState({
    taskName:           cellText(f["Task Name"])           || "",
    taskDescription:    cellText(f["Task Description"])    || "",
    priority:           cellText(f["Priority"])            || "Medium",
    recurringType:      cellText(f["Recurring Type"])      || "Not Applicable",
    companyName:        cellText(f["Company Name"])        || "Aquint Infratele India Pvt Ltd",
    assigneeDepartment: cellText(f["Assignee Department"]) || "HR & ADMIN",
    creatorDepartment:  cellText(f["Creator Department"])  || "HR & ADMIN",
    startDate:          toDatetimeLocal(f["Start Date"]),
    endDate:            toDatetimeLocal(f["End Date"]),
    supervisorId:       getSupervisorId(f["Supervisor"]),
    assignedUserIds:    getAssignedUserIds(f["Assigned Users"]),
  });

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [attachments,    setAttachments]    = useState([]); // existing
  const [stagedFiles,    setStagedFiles]    = useState([]); // to be uploaded
  const [loadingAttach, setLoadingAttach] = useState(false);
  
  const loadAttachments = useCallback(async () => {
    try {
      setLoadingAttach(true);
      const list = await getAttachments(rid);
      setAttachments(list);
    } catch(err) { console.error("Failed to load attachments:", err); }
    finally { setLoadingAttach(false); }
  }, [getAttachments, rid]);

  useEffect(() => { loadAttachments(); }, [loadAttachments]);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    if (!form.taskName.trim()) return setError("Task Name is required.");
    setError(""); setLoading(true);
    try {
      const fields = {
        "Task Name":           form.taskName,
        "Task Description":    form.taskDescription,
        "Priority":            form.priority,
        "Recurring Type":      form.recurringType,
        "Company Name":        form.companyName,
        "Assignee Department": form.assigneeDepartment,
        "Creator Department":  form.creatorDepartment,
        ...(form.startDate && { "Start Date": new Date(form.startDate).getTime() }),
        ...(form.endDate   && { "End Date":   new Date(form.endDate).getTime() }),
      };

      fields["Assigned Users"] = form.assignedUserIds.length > 0 
        ? form.assignedUserIds.map(id => ({ id })) 
        : null;

      if (form.supervisorId) {
        fields["Supervisor"] = [{ id: form.supervisorId }];
      } else {
        fields["Supervisor"] = null;
      }

      await updateTask(tableId, record.record_id, fields);

      // Upload staged files
      if (stagedFiles.length > 0) {
        const taskIdVal = cellText(f["Task ID"]) || rid;
        for (const file of stagedFiles) {
          try { await uploadFile(file, String(taskIdVal)); }
          catch(e) { console.warn("Upload failed:", e.message); }
        }
      }

      onClose();
    } catch (e) {
      setError(e.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h3>✏️ Edit Task — {cellText(f["Task ID"]) || record.record_id}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">⚠ {error}</div>}

          <div className="form-group">
            <label className="form-label">Task Name <span>*</span></label>
            <input className="form-input" value={form.taskName}
              onChange={e => set("taskName", e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Task Description</label>
            <textarea className="form-textarea" value={form.taskDescription}
              onChange={e => set("taskDescription", e.target.value)} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <CustomSelect 
              label="Assigned Users *"
              multiple={true}
              placeholder="-- Select Assigned Users --"
              options={larkUsers}
              value={form.assignedUserIds}
              onChange={val => set("assignedUserIds", val)}
            />
          </div>

          <div className="form-grid">
            <CustomSelect 
              label="Supervisor"
              placeholder="-- Select Supervisor --"
              options={larkUsers}
              value={form.supervisorId}
              onChange={val => set("supervisorId", val)}
              isClearable={true}
            />

            <CustomSelect 
              label="Priority"
              options={PRIORITIES}
              value={form.priority}
              onChange={val => set("priority", val)}
            />

            <CustomSelect 
              label="Recurring Type"
              options={RECURRING_TYPES}
              value={form.recurringType}
              onChange={val => set("recurringType", val)}
            />

            <CustomSelect 
              label="Company"
              options={COMPANIES}
              value={form.companyName}
              onChange={val => set("companyName", val)}
            />

            <CustomSelect 
              label="Assignee Department"
              options={DEPARTMENTS}
              value={form.assigneeDepartment}
              onChange={val => set("assigneeDepartment", val)}
            />

            <CustomSelect 
              label="Creator Department"
              options={DEPARTMENTS}
              value={form.creatorDepartment}
              onChange={val => set("creatorDepartment", val)}
            />

            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input type="datetime-local" className="form-input" value={form.startDate}
                onChange={e => set("startDate", e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">End Date</label>
              <input type="datetime-local" className="form-input" value={form.endDate}
                onChange={e => set("endDate", e.target.value)} />
            </div>
          </div>

          <hr style={{ margin: "24px 0", border: "0", borderTop: "1px solid var(--gray-200)" }} />

          {/* Current Attachments */}
          <div className="form-group">
            <label className="form-label" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>📎 Current Attachments</span>
              {loadingAttach && <span style={{ fontSize: 11, color: "var(--gray-400)" }}>Refreshing...</span>}
            </label>
            {attachments.length === 0 && !loadingAttach ? (
              <div style={{ padding: "12px", border: "1px dashed var(--gray-200)", borderRadius: 10, textAlign: "center", fontSize: 13, color: "var(--gray-400)" }}>
                No attachments found.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {attachments.map((a, i) => (
                  <div key={i} style={{ 
                    display: "flex", alignItems: "center", justifyContent: "space-between", 
                    background: "var(--gray-50)", border: "1px solid var(--gray-200)", 
                    borderRadius: 10, padding: "8px 12px"
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--gray-700)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        📄 {a.fileName}
                      </span>
                      <span style={{ fontSize: 10, color: "var(--gray-400)" }}>By {a.uploaderName || "Unknown"}</span>
                    </div>
                    <button 
                      type="button"
                      onClick={async () => {
                        if (window.confirm(`Permanently delete "${a.fileName}"?`)) {
                          try { 
                            await deleteAttachment(cellText(f["Task ID"]) || rid, a.fileToken); 
                            loadAttachments(); 
                          } catch(e) { alert(e.message); }
                        }
                      }}
                      className="btn-icon" style={{ color: "var(--danger)", padding: 4 }}>🗑️</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* New Attachments */}
          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">📤 Add New Attachments</label>
            <div
              style={{
                border: "2px dashed var(--gray-300)", borderRadius: 12, padding: "20px",
                textAlign: "center", background: "var(--gray-50)", cursor: "pointer", transition: "all 0.2s"
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "var(--primary)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--gray-300)"}
              onClick={() => document.getElementById("edit-file-input").click()}
            >
              <div style={{ fontSize: 24, marginBottom: 4 }}>📎</div>
              <div style={{ fontSize: 14, color: "var(--gray-600)", fontWeight: 500 }}>Click to high-quality files to add</div>
              <input id="edit-file-input" type="file" multiple style={{ display: "none" }}
                onChange={e => setStagedFiles(p => [...p, ...Array.from(e.target.files)])} />
            </div>
            {stagedFiles.length > 0 && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--primary)", textTransform: "uppercase" }}>Ready to upload:</div>
                {stagedFiles.map((file, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "var(--primary-light)", border: "1px solid var(--primary-border)",
                    borderRadius: 10, padding: "8px 12px", fontSize: 13,
                  }}>
                    <span style={{ color: "var(--primary-dark)", fontWeight: 500 }}>📄 {file.name}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setStagedFiles(p => p.filter((_,j) => j !== i)); }}
                      style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? "🔄 Saving Changes..." : "💾 Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );

  // Render the modal directly into document.body to break out of any parent CSS transforms/filters
  return ReactDOM.createPortal(modalContent, document.body);
}
